import React, { useState } from 'react';
import CompactMealCard from 'components/shared/CompactMealCard';
import ScrollArrows from 'components/shared/ScrollArrows';
import AddonInfoModal from 'components/storefront/AddonInfoModal';
import SubscriptionMealCard, { subscriptionMealImage } from 'components/shared/SubscriptionMealCard';
import ProteinTier from 'components/shared/ProteinTier';
import { PREMIUM_SURCHARGE } from 'constants/meals';
import { useScrollArrows } from 'hooks/useScrollArrows';
import { quizNutritionForMealType } from 'utils/nutritionGoals';
import { groupAddOnsByCategory } from 'utils/menuSections';

// Same scroll-row key the a-la-carte menu uses per section (e.g. "addons" is
// one of its section ids) — reusing the identical hook/component here too.
const ADDONS_SCROLL_KEY = 'addons';

// Rough wireframe pricing — placeholders until Yang confirms where the real
// per-meal base prices and the subscription discount should live (Airtable?).
export const SUBSCRIPTION_BASE_PRICES = {
  breakfast: 12.99,
  lunch: 19.99,
  dinner: 23.99,
  snack: 4.99,
};
export const SUBSCRIPTION_DISCOUNT = 0.9;

// `mealType` is the canonical singular form (matches constants/meals.js and
// quizAPI-control's mealBreakdown keys via quizNutritionForMealType) — kept
// separate from `label`, which is plural for snacks for display.
export const MEAL_TYPES = [
  { key: 'breakfast', label: 'Tailored Breakfast', mealType: 'Breakfast' },
  { key: 'lunch', label: 'Tailored Lunch', mealType: 'Lunch' },
  { key: 'dinner', label: 'Tailored Dinner', mealType: 'Dinner' },
  { key: 'snack', label: 'Tailored Snacks', mealType: 'Snack' },
];

// Placeholder — priced like a lunch until the real additional-meal price is set.
const ADDITIONAL_MEAL_PRICE = SUBSCRIPTION_BASE_PRICES.lunch;

/** "Additional Meal #n" rows the quiz added to this member's Client rows, in
 *  order. Their key in `subscription.meals` is the Client `Meal` value itself. */
export function additionalMealTypes(householdMember) {
  return Object.keys(householdMember?.profile?.rowTargets || {})
    .filter((meal) => /^Additional Meal #\d+$/.test(meal))
    .sort();
}

/** Same priority as MenuPage's calorieInfoForMember, minus the Skip Portioning
 *  case — there's no per-dish catalog here, just meal-type quantities. */
export function nutritionInfoForMealType(mealType, householdMember, defaultMealTargets = {}, qty = 0) {
  if (householdMember?.hideNutrition) return null;
  const fallback = defaultMealTargets[mealType];
  // A meal the quiz skipped reads 0 until it's added, then the default targets it's portioned with.
  const skipped = householdMember?.skippedMeals ?? householdMember?.profile?.skippedMeals ?? [];
  if (skipped.some((m) => String(m).replace(/s$/, '') === mealType.replace(/s$/, ''))) {
    if (qty > 0 && fallback) return { source: 'default', ...fallback };
    return { source: 'skipped', calories: 0, carbsG: 0, proteinG: 0, fatG: 0, fiberG: 0 };
  }
  return quizNutritionForMealType(mealType, householdMember)
    ?? (fallback ? { source: 'default', ...fallback } : null);
}

export function createDefaultSubscriptionMember(overrides = {}) {
  return {
    meals: { breakfast: 0, lunch: 0, dinner: 0, snack: 0 },
    premium: {}, // meal key -> true for organic / wild-caught / grass-fed
    addons: {}, // dishId -> qty
    ...overrides,
  };
}

// `addons` is the real catalog — /api/menu's dishes filtered to mealType
// "Add On" (see SubscriptionBuilderPage) — not mock data, so this needs it
// passed in rather than reading a hardcoded list.
export function memberSubtotalCents(member, addons = []) {
  const mealCents = Object.entries(member.meals || {}).reduce((sum, [key, qty]) => {
    const price = SUBSCRIPTION_BASE_PRICES[key] ?? ADDITIONAL_MEAL_PRICE;
    return sum + (qty || 0) * price * 100;
  }, 0);
  // The premium surcharge is per meal, not on snacks, and not discounted (same as the account panel).
  const premiumCents = Object.entries(member.meals || {}).reduce((sum, [key, qty]) => (
    key !== 'snack' && member.premium?.[key] ? sum + (qty || 0) * PREMIUM_SURCHARGE * 100 : sum
  ), 0);
  const discountedMealCents = mealCents * SUBSCRIPTION_DISCOUNT + premiumCents;

  const addonCents = addons.reduce((sum, addon) => {
    const qty = member.addons?.[addon.dishId] || 0;
    return sum + qty * (Number(addon.price) || 0) * 100;
  }, 0);

  return Math.round(discountedMealCents + addonCents);
}

function formatDollars(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function SubscriptionMemberPanel({ subscription, householdMember, addons = [], addonsLoading = false, defaultMealTargets = {}, onChangeMember }) {
  const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll } = useScrollArrows();
  const [infoAddonId, setInfoAddonId] = useState(null);
  const infoAddon = addons.find((a) => a.dishId === infoAddonId) || null;

  const updateMeal = (key, qty) => {
    onChangeMember({ ...subscription, meals: { ...subscription.meals, [key]: qty } });
  };

  const updatePremium = (key, premium) => {
    onChangeMember({ ...subscription, premium: { ...subscription.premium, [key]: premium } });
  };

  const updateAddon = (addonId, qty) => {
    const addons = { ...subscription.addons };
    if (qty <= 0) {
      delete addons[addonId];
    } else {
      addons[addonId] = qty;
    }
    onChangeMember({ ...subscription, addons });
  };

  const subtotalCents = memberSubtotalCents(subscription, addons);

  return (
    <div className="sf-sub-panel">
      <section className="sf-sub-section">
        <h3 className="sf-sub-section__title">Weekly meals</h3>
        <p className="sf-sub-section__hint">
          Choose how many of each meal per week (for Tailored Snacks, how many days
          per week; the price is per day, however many snacks you have a day). The actual dishes are picked
          from each week&apos;s menu closer to delivery — this just sets quantities.
        </p>
        <div className="sf-sub-stepper-grid">
          {MEAL_TYPES.map(({ key, label, mealType }) => {
            const qty = subscription.meals?.[key] || 0;
            const nutrition = nutritionInfoForMealType(mealType, householdMember, defaultMealTargets, qty);
            const premium = key !== 'snack' && !!subscription.premium?.[key];
            const unit = SUBSCRIPTION_BASE_PRICES[key] * SUBSCRIPTION_DISCOUNT + (premium ? PREMIUM_SURCHARGE : 0);
            const snacksPerDay = key === 'snack' ? Number(householdMember?.snacksPerDay ?? householdMember?.profile?.mealBreakdown?.Snacks?.count) : 0;
            return (
              <SubscriptionMealCard
                key={key}
                label={key === 'snack' ? 'Tailored Snacks' : label}
                image={subscriptionMealImage(key)}
                price={unit}
                unit={`${key === 'snack' ? '/ day' : 'ea'} · 10% off`}
                note={snacksPerDay > 0 ? `${snacksPerDay} snack${snacksPerDay > 1 ? 's' : ''} per day` : null}
                nutrition={nutrition}
                qty={qty}
                onChange={(n) => updateMeal(key, n)}
              >
                {key !== 'snack' && (
                  <ProteinTier premium={premium} onChange={(p) => updatePremium(key, p)} />
                )}
              </SubscriptionMealCard>
            );
          })}
          {additionalMealTypes(householdMember).map((mealType) => {
            const qty = subscription.meals?.[mealType] || 0;
            const premium = !!subscription.premium?.[mealType];
            return (
              <SubscriptionMealCard
                key={mealType}
                label={mealType}
                price={ADDITIONAL_MEAL_PRICE * SUBSCRIPTION_DISCOUNT + (premium ? PREMIUM_SURCHARGE : 0)}
                unit="ea · 10% off"
                nutrition={nutritionInfoForMealType(mealType, householdMember, defaultMealTargets, qty)}
                qty={qty}
                onChange={(n) => updateMeal(mealType, n)}
              >
                <ProteinTier premium={premium} onChange={(p) => updatePremium(mealType, p)} />
              </SubscriptionMealCard>
            );
          })}
        </div>
      </section>

      <section className="sf-sub-section">
        <h3 className="sf-sub-section__title">Add-ons</h3>
        <p className="sf-sub-section__hint">Extra protein or items on top of your meals.</p>
        {addonsLoading && <p className="sf-sub-section__hint">Loading add-ons…</p>}
        {/* Same CompactMealCard + ScrollArrows/useScrollArrows the a-la-carte
            menu uses for each section — one scrollable row with arrows that
            appear once there are more cards than fit, not a wrapping grid. */}
        {groupAddOnsByCategory(addons).map((group, _, groups) => {
          const scrollKey = `${ADDONS_SCROLL_KEY}-${group.id}`;
          return (
            <React.Fragment key={group.id}>
              {groups.length > 1 && <h4 className="sf-sub-section__subtitle">{group.label}</h4>}
              <div className="sf-menu-scroll-wrap">
                <div
                  className="sf-menu-scroll-row"
                  data-meal-type={scrollKey}
                  onScroll={(e) => handleScroll(scrollKey, e)}
                >
                  {group.items.map((addon) => (
                    <CompactMealCard
                      key={addon.dishId}
                      meal={{
                        ...addon,
                        itemName: addon.name,
                        quantity: subscription.addons?.[addon.dishId] || 0,
                      }}
                      actualIndex={addon.dishId}
                      // AddonInfoModal, not the real MealModal — see its own comment
                      // for why (no ingredient substitution applies to a fixed-recipe
                      // add-on item).
                      onOpenModal={() => setInfoAddonId(addon.dishId)}
                      onQuantityChange={(_, qty) => updateAddon(addon.dishId, qty)}
                      isLocked={false}
                      saving={false}
                      addLabel="Add"
                      // Details live in AddonInfoModal now (click the card) — the
                      // inline ingredient listing was redundant clutter here.
                      showIngredients={false}
                    />
                  ))}
                </div>
                <ScrollArrows
                  mealType={scrollKey}
                  shouldShowArrows={shouldShowArrows}
                  itemCount={group.items.length}
                  scrollState={getScrollState(scrollKey)}
                  onScrollLeft={scrollLeft}
                  onScrollRight={scrollRight}
                />
              </div>
            </React.Fragment>
          );
        })}
      </section>

      <div className="sf-sub-panel__subtotal">
        <span>Weekly subtotal for this person</span>
        <strong>{formatDollars(subtotalCents)}</strong>
      </div>

      {infoAddon && (
        <AddonInfoModal
          addon={infoAddon}
          qty={subscription.addons?.[infoAddon.dishId] || 0}
          onChangeQty={(qty) => updateAddon(infoAddon.dishId, qty)}
          onClose={() => setInfoAddonId(null)}
        />
      )}
    </div>
  );
}
